import React, { FC, useEffect, useState } from 'react'
import Modal from '../common/Modal'
import { nowDate } from '../../../utils/date'
import iconv = require('iconv-lite')
import Encoding from 'encoding-japanese'
import { useAlert } from 'react-alert'
import { ResponsePostTaskLabelMasterImport } from '../../../states/types/task'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import COLOR_LIST from '../../constants/taskColor'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FileDownload = require('js-file-download')

interface Props {
  isDisplay: boolean
  dataList: ResponsePostTaskLabelMasterImport | undefined
  isLoading: boolean
  closeCallback: VoidFunction
  addCallback: (importParams:{ csv_file: string, register: boolean }, modalCallback: VoidFunction) => void
}

const IMPORT_LIMIT = 100

const ImportLabelMasterModal: FC<Props> = (props) => {
  const {
    isDisplay,
    dataList,
    isLoading,
    closeCallback,
    addCallback,
  } = props

  const alert = useAlert()

  const [fileData, setFileData] = useState("")
  const [step, setStep] = useState<number>(1)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [csvErrorList, setCsvErrorList] = useState<{column_name: string, row_number: number, message: string}[]>([])

  const downloadCsv = (code: string) => {
    const resultRows = "業務名,業務カラー,業務の説明"
    const filename = `業務一括登録_${nowDate("YYYYMMDD")}`.slice(0, 150)
    if (code === 'Shift-JIS') {
      const encodeData = iconv.encode(resultRows, "windows-31j")
      FileDownload(new Blob([encodeData]), `${filename}.csv`)
    } else {
      FileDownload(resultRows, `${filename}.csv`)
    }
  }

  const uploadCsv = (uploadFile: FileList | null) => {
    setErrorMessage("")
    if (!uploadFile || uploadFile[0].type !== "text/csv") {
      setErrorMessage("不正なファイルを検知しました")
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target === null || e.target.result === null) return
      const buf = new Uint8Array(e.target.result as ArrayBuffer)
      const codes = Encoding.detect(new Uint8Array(buf))
      if (codes === 'UNICODE' || codes === 'SJIS') {
        setFileData(iconv.decode(Buffer.from(buf), "windows-31j"))
        return
      }

      setFileData(new TextDecoder("utf-8").decode(buf))
    }
    reader.onerror = () => alert.error("不正なファイルが読み込まれました")
    reader.readAsArrayBuffer(uploadFile[0])
  }

  const prev = () => {
    setStep(step - 1)
    setFileData("")
    setErrorMessage("")
    setCsvErrorList([])
  }

  const submit = () => {
    if (dataList?.errors.length) return
    addCallback({ csv_file: fileData, register: true }, () => setStep(5))
  }

  const buildButtonArea = () => {
    let prevButton = (
      <button
        type="button"
        className="button cancel"
        onClick={prev}
      >
        戻る
      </button>
    )
    let nextButton = (
      <button
        type="button"
        className="button"
        onClick={() => setStep(step + 1)}
        disabled={step === 3 && (!fileData || !!dataList?.errors.length || !!errorMessage)}
      >
        次へ
      </button>
    )
    if (step === 1) {
      prevButton = <></>
    }
    if (step === 4) {
      nextButton = (
        <button
          type="button"
          className="button"
          onClick={submit}
        >
          一括登録する
        </button>
      )
    }
    if (step === 5) {
      prevButton = <></>
      nextButton = (
        <button
          type="button"
          className="button cancel"
          onClick={() => closeCallback()}
        >
          閉じる
        </button>
      )
    }
    if (isLoading) {
      prevButton = (
        <button
          type="button"
          className="button cancel"
          disabled
        >
          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
        </button>
      )
      nextButton = (
        <button
          type="button"
          className="button"
          disabled
        >
          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
        </button>
      )
    }
    return (
      <div className="button_area">
        {prevButton}
        {nextButton}
      </div>
    )
  }

  useEffect(() => {
    setCsvErrorList(dataList?.errors || [])
  }, [dataList?.errors])

  useEffect(() => {
    setStep(1)
    setFileData("")
    setErrorMessage("")
    setCsvErrorList([])
  }, [isDisplay])

  useEffect(() => {
    if (!fileData) return
    const parseRow = fileData.split(/\r\n|\n|\r/)
    if (
      (parseRow[0] !== "業務名,業務カラー,業務の説明")
    ) {
      setErrorMessage("指定外のCSVファイルを検知しました")
    } else if (parseRow.length > IMPORT_LIMIT + 1) {
      setErrorMessage(`一度に一括登録できる業務数は${IMPORT_LIMIT}件までです`)
    } else {
      setCsvErrorList([])
      addCallback({ csv_file: fileData, register: false }, () => { /**/ })
    }
  }, [fileData])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="import_label_master"
      closeCallback={closeCallback}
      closeable
    >
      <>
        <div className="modal_title">
          業務を一括登録する
        </div>
        {
          step === 1 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.1 業務一括登録用CSVファイルのダウンロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      業務一括登録用CSVファイルのダウンロードをおこない、編集してください。
                    </div>
                    <div className="red">
                      ※ExcelでCSVを開く方はShift-JISを選択して下さい。
                    </div>
                  </div>
                  <div className="detail_button_area">
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('Shift-JIS')}
                    >
                      業務一括登録用(Shift-JIS)
                    </button>
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('UTF-8')}
                    >
                      業務一括登録用(UTF-8)
                    </button>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === 2 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.2 業務一括登録用CSVファイルの編集
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      <p>
                        ダウンロードした業務一括登録用CSVファイルを編集してください。
                      </p>
                      <p>
                        {`一度に一括登録できる業務数は${IMPORT_LIMIT}件までです。`}
                      </p>
                    </div>
                    <div
                      style={{
                        maxHeight: "400px",
                        overflowY: "scroll",
                      }}
                    >
                      <table>
                        <tr>
                          <th>
                            項目名
                          </th>
                          <th>
                            必須 / 任意
                          </th>
                          <th>
                            概要
                          </th>
                        </tr>
                        <tr>
                          <td>
                            業務名
                          </td>
                          <td>
                            <p className="required">
                              必須
                            </p>
                          </td>
                          <td>
                            <p>
                              業務名を入力してください。
                            </p>
                            <p className="example">
                              例) テスト業務
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            業務カラー
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              業務にカラーを設定したい場合、入力してください。
                            </p>
                            <p className="example">
                              例) レッド
                            </p>
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                padding: "8px",
                                margin: "8px 0",
                              }}
                            >
                              <div
                                style={{
                                  marginBottom: "8px",
                                }}
                              >
                                設定できるカラー一覧
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "8px",
                                }}
                              >
                                {
                                  COLOR_LIST.map((color) => {
                                    if (color.name === "グレースケール") return (<></>)
                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                        }}
                                      >
                                        {
                                          color.values.map((v) => (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                fontSize: "10px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  backgroundColor: v.color,
                                                  width: "10px",
                                                  height: "10px",
                                                  borderRadius: "50%",
                                                }}
                                              />
                                              {v.text}
                                            </div>
                                          ))
                                        }
                                      </div>
                                    )
                                  })
                                }
                              </div>
                            </div>
                            <p>
                              入力なしの場合はランダムに設定されます。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            業務の説明
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              業務に説明を設定したい場合、入力してください。
                            </p>
                            <p className="example">
                              例) 〇〇のときはこの業務を選択してください
                            </p>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === 3 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.3 業務一括登録用CSVファイルのアップロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      編集した業務一括登録用CSVファイルをアップロードしてください。
                    </div>
                  </div>
                  <div className="upload_area">
                    <label htmlFor="import_csv">
                      業務一括登録用CSVファイルをアップロードしてください。
                      <input
                        type="file"
                        id="import_csv"
                        name="import_csv"
                        accept="text/csv"
                        onChange={(e) => uploadCsv(e.currentTarget.files)}
                        onClick={(e) => { e.currentTarget.value = "" }}
                      />
                    </label>
                  </div>
                  {
                    errorMessage ? (
                      <div className="error_message">
                        {errorMessage}
                      </div>
                    ) : (
                      <></>
                    )
                  }
                  {
                    !!csvErrorList.length && (
                      <div className="error_message">
                        {
                          csvErrorList.map((error) => (
                            <p>
                              {`${error.row_number}行目:${error.column_name !== "共通" ? `[${error.column_name}]` : ""}${error.message}`}
                            </p>
                          ))
                        }
                      </div>
                    )
                  }
                </div>
              </div>
            </>
          )
        }
        {
          step === 4 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.4 追加するプロジェクトの確認
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      以下の内容で
                      <span style={{ fontWeight: "bold" }}>
                        全{dataList?.label_masters?.length ?? 0}件
                      </span>の業務の登録をおこないます。
                    </div>
                  </div>
                  <div className="detail_result_area">
                    <div className="result_row_header">
                      <div className="name_area">
                        業務名
                      </div>
                      <div className="detail_area">
                        業務の説明
                      </div>
                    </div>
                    {
                      dataList?.label_masters.map((label_master) => (
                        <div
                          key={`data_list_${label_master.name}`}
                          className="result_row"
                        >
                          <div className="name_area">
                            <div
                              className="icon_color"
                              style={{
                                backgroundColor: label_master.color || "#CCCCCC",
                              }}
                            />
                            {label_master.name}
                          </div>
                          <div className="detail_area">
                            {label_master.description}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === 5 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  業務一括登録が完了しました
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      <p>
                        業務一括登録が完了しました。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {buildButtonArea()}
      </>
    </Modal>
  )
}

export default ImportLabelMasterModal
